/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Pre-confirmación de export CSV con alcance explícito (proto ventas-ux-v1 §7):
 * período + filtros activos + cantidad estimada + cap. El export recorre todas
 * las páginas del servidor — nunca solo la página visible.
 */
import React from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, ticketColors } from '../../shared/theme/public-api';
import { ticketChrome as ticketUi } from '../../shared/ticket/ticketChrome.styles';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';
import { TicketAlertStrip } from '../../shared/ticket/TicketAlertStrip';
import { TicketButton } from '../../shared/ticket/TicketButton';
import { SALES_LIST_EXPORT_MAX_ROWS } from '../infra/salesListExport';

const WIDE_BREAKPOINT = 768;

type Props = {
  visible: boolean;
  /** Resumen de alcance (período + filtros + orden) — buildSalesExportScopeSummary. */
  scopeSummary: string;
  /** Cantidad estimada = totalCount del query con filtros aplicados (null = aún no cargó). */
  estimatedCount: number | null;
  exporting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export function SalesExportConfirmModal({
  visible,
  scopeSummary,
  estimatedCount,
  exporting = false,
  onClose,
  onConfirm,
}: Props) {
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const asDialog = width >= WIDE_BREAKPOINT;
  const countLabel =
    estimatedCount == null
      ? 'Ventas'
      : estimatedCount > 0
        ? `${estimatedCount} venta${estimatedCount === 1 ? '' : 's'}`
        : 'Sin ventas';

  return (
    <Modal
      visible={visible}
      transparent
      animationType={asDialog ? 'fade' : 'slide'}
      onRequestClose={onClose}
      accessibilityViewIsModal
    >
      <View style={[ticketUi.sheetBackdrop, asDialog && styles.backdropDialog]}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View
          style={[
            asDialog ? ticketUi.dialogCard : styles.sheet,
            asDialog && styles.dialogWidth,
            {
              paddingBottom: asDialog ? 0 : Math.max(insets.bottom, spacing.sm),
              maxHeight: height * 0.92,
            },
          ]}
        >
          {!asDialog ? <View style={ticketUi.grabber} /> : null}
          <TicketModalHeader
            kicker="Ventas"
            title="Exportar CSV"
            subtitle={`${countLabel} con los filtros actuales · todas las páginas`}
            onClose={onClose}
            compact={!asDialog}
          />
          <View style={styles.body}>
            <TicketAlertStrip
              title="Alcance"
              subtitle={scopeSummary}
              tone="default"
              hideChevron
              testID="ventas.export.scope"
            />
            {estimatedCount === 0 ? (
              <TicketAlertStrip
                title="Sin resultados"
                subtitle="No hay ventas para exportar con esos filtros."
                tone="bad"
                hideChevron
                testID="ventas.export.empty"
              />
            ) : null}
            <TicketAlertStrip
              title="Máximo por archivo"
              subtitle={`Si supera las ${SALES_LIST_EXPORT_MAX_ROWS.toLocaleString('es-AR')} ventas, exporto las primeras ${SALES_LIST_EXPORT_MAX_ROWS.toLocaleString('es-AR')} y te aviso.`}
              tone="warn"
              hideChevron
              testID="ventas.export.cap"
            />
          </View>
          <View style={ticketUi.foot}>
            <TicketButton
              label="Cancelar"
              onPress={onClose}
              disabled={exporting}
              testID="ventas.export.cancel"
            />
            <TicketButton
              label={exporting ? 'Generando…' : 'Descargar CSV'}
              variant="gold"
              onPress={onConfirm}
              disabled={exporting || estimatedCount === 0}
              accessibilityLabel="Descargar CSV de ventas"
              testID="ventas.export.confirm"
              style={styles.confirmBtn}
            />
            {exporting ? (
              <ActivityIndicator size="small" color={ticketColors.gold} style={styles.spinner} />
            ) : null}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdropDialog: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  dialogWidth: {
    width: '100%',
    maxWidth: 460,
  },
  sheet: {
    width: '100%',
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  body: {
    gap: spacing.sm,
    paddingHorizontal: 16,
    paddingVertical: spacing.md,
  },
  confirmBtn: {
    flexGrow: 1,
  },
  spinner: {
    alignSelf: 'center',
  },
});
